/**
 * The full 1–75 called-number board.
 *
 * Unlike `BallCall` (which deliberately shows only the last few draws so it
 * doesn't compete with the card), this is a verification view: every number
 * that has been called, always visible, so a player can check the board
 * against their own card at a glance. Shown beside the card rather than in
 * place of it.
 */

import { memo } from 'react';

const COLUMNS = [
  { letter: 'B', from: 1 },
  { letter: 'I', from: 16 },
  { letter: 'N', from: 31 },
  { letter: 'G', from: 46 },
  { letter: 'O', from: 61 },
] as const;

interface CalledBoardProps {
  calledSet: ReadonlySet<number>;
  lastNumber: number | null;
  /**
   * Big display variant for the spectator (not-joined) view, where there is no
   * card competing for the screen — cells grow so every number is readable.
   * Joined players keep the compact verification board beside their card.
   */
  large?: boolean;
}

export const CalledBoard = memo(function CalledBoard({
  calledSet,
  lastNumber,
  large = false,
}: CalledBoardProps) {
  return (
    <div
      className={['calledboard', large ? 'calledboard--large' : ''].filter(Boolean).join(' ')}
      role="table"
      aria-label="Called numbers"
    >
      <div className="calledboard__header" aria-hidden="true">
        {COLUMNS.map((c) => (
          <span key={c.letter} className={`calledboard__letter calledboard__letter--${c.letter}`}>
            {c.letter}
          </span>
        ))}
      </div>
      <div className="calledboard__grid">
        {COLUMNS.map((c) => (
          <div key={c.letter} className="calledboard__col">
            {Array.from({ length: 15 }, (_, i) => c.from + i).map((n) => {
              const called = calledSet.has(n);
              const isLast = n === lastNumber;
              return (
                <span
                  key={n}
                  className={[
                    'calledboard__cell',
                    called ? 'calledboard__cell--called' : '',
                    isLast ? 'calledboard__cell--last' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {n}
                </span>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
});
